import { PlayerListItemFragment } from '@amf/shared/types/graphql';
import usePlayerStats from '@amf/shared/data/hooks/usePlayerStats';
import { useTranslate } from '@tolgee/react';
import Tippy from '@tippyjs/react';
import React, { Fragment } from 'react';
import TeamAvatar from '@amf/shared/components/team/TeamAvatar';

interface PlayerStatsProps {
  player: PlayerListItemFragment;
  title?: string;
  disableAssists?: boolean;
  disablePoints?: boolean;
}

function PlayerStats({ player, title, disableAssists, disablePoints }: PlayerStatsProps) {
  const { t } = useTranslate();
  const { stats, summary } = usePlayerStats(player.id);

  if (stats.length === 0) return null;
  return (
    <div className='PlayerStats'>
      {title && <h2 className='PlayerStats__title'>{title}</h2>}
      <div className='PlayerStats__content'>
        <table>
          <thead>
            <tr>
              <th className='left'>{t('player.stats.season')}</th>
              <th className='left'>{t('player.stats.league')}</th>
              <th>{t('player.stats.matches')}</th>
              <th>{t('player.stats.goals')}</th>
              {!disableAssists && <th>{t('player.stats.assists')}</th>}
              {!disablePoints && <th>{t('player.stats.points')}</th>}
              <th>{t('player.stats.yellowCards')}</th>
              <th>{t('player.stats.redCards')}</th>
            </tr>
          </thead>
          <tbody>
            {stats &&
              stats.map(item => (
                <tr key={item.id}>
                  <td className='left'>
                    <strong>{item.season.name}</strong>
                  </td>
                  <td className='left PlayerStats__league'>
                    <strong>{item.leagueName}</strong>
                    {item.teams.map(team => (
                      <Fragment key={team.id}>
                        <Tippy content={team.name} animation='shift-away' theme='light'>
                          <span>
                            <TeamAvatar team={team} className='PlayerStats__team' />
                          </span>
                        </Tippy>
                      </Fragment>
                    ))}
                  </td>
                  <td>{item.matches}</td>
                  <td>
                    {item.goals}{' '}
                    {item.matches > 0 && (
                      <Tippy
                        content={t('player.stats.goals.avg')}
                        animation='shift-away'
                        theme='light'
                      >
                        <small className='PlayerStats__avg'>
                          ({(item.goals / item.matches).toFixed(1)})
                        </small>
                      </Tippy>
                    )}
                  </td>
                  {!disableAssists && (
                    <td>
                      {item.assists}{' '}
                      {item.matches > 0 && (
                        <Tippy
                          content={t('player.stats.assists.avg')}
                          animation='shift-away'
                          theme='light'
                        >
                          <small className='PlayerStats__avg'>
                            ({(item.assists / item.matches).toFixed(1)})
                          </small>
                        </Tippy>
                      )}
                    </td>
                  )}
                  {!disablePoints && (
                    <td>
                      {item.points}{' '}
                      {item.matches > 0 && (
                        <Tippy
                          content={t('player.stats.points.avg')}
                          animation='shift-away'
                          theme='light'
                        >
                          <small className='PlayerStats__avg'>
                            ({(item.points / item.matches).toFixed(1)})
                          </small>
                        </Tippy>
                      )}
                    </td>
                  )}
                  <td>{item.yellowCards}</td>
                  <td>{item.redCards}</td>
                </tr>
              ))}
          </tbody>
          {summary && (
            <tfoot>
              <tr key={summary.id}>
                <th className='left' colSpan={2}>
                  <strong>{t('player.stats.summary')}</strong>
                </th>
                <th className='fs-normal'>{summary.matches}</th>
                <th className='fs-normal'>
                  {summary.goals}{' '}
                  {summary.matches > 0 && (
                    <Tippy
                      content={t('player.stats.goals.avg')}
                      animation='shift-away'
                      theme='light'
                    >
                      <small className='PlayerStats__avg'>
                        ({(summary.goals / summary.matches).toFixed(1)})
                      </small>
                    </Tippy>
                  )}
                </th>
                {!disableAssists && (
                  <th className='fs-normal'>
                    {summary.assists}{' '}
                    {summary.matches > 0 && (
                      <Tippy
                        content={t('player.stats.assists.avg')}
                        animation='shift-away'
                        theme='light'
                      >
                        <small className='PlayerStats__avg'>
                          ({(summary.assists / summary.matches).toFixed(1)})
                        </small>
                      </Tippy>
                    )}
                  </th>
                )}
                {!disablePoints && (
                  <th className='fs-normal'>
                    {summary.points}{' '}
                    {summary.matches > 0 && (
                      <Tippy
                        content={t('player.stats.points.avg')}
                        animation='shift-away'
                        theme='light'
                      >
                        <small className='PlayerStats__avg'>
                          ({(summary.points / summary.matches).toFixed(1)})
                        </small>
                      </Tippy>
                    )}
                  </th>
                )}
                <th className='fs-normal'>{summary.yellowCards}</th>
                <th className='fs-normal'>{summary.redCards}</th>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

export default PlayerStats;
